(() => {
  const config = window.TFBTradeDeliveryConfig || {};
  const chargeVariantId = Number(config.variantId || 0);
  const chargeSku = String(config.sku || 'TFB-TRADE-DELIVERY').trim().toUpperCase();
  const chargeHandle = String(config.handle || 'trade-delivery-charge-8');
  const minimumTradeWeight = Number(config.minimumTradeWeight || 8000);
  const isTradeCustomer = Boolean(config.isTradeCustomer);
  const isB2BCustomer = Boolean(config.isB2BCustomer);
  const currentProductHandle = String(config.currentProductHandle || '');
  const restrictCurrentB2BVariants = Boolean(config.restrictCurrentB2BVariants);
  const eligibleB2BHandles = new Set(
    Array.isArray(config.b2bEligibleHandles) ? config.b2bEligibleHandles.map(String) : []
  );
  const eligibleB2BProductIds = new Set(
    Array.isArray(config.b2bEligibleProductIds)
      ? config.b2bEligibleProductIds.map(Number)
      : []
  );
  const eligibleB2BVariantIds = new Set(
    Array.isArray(config.b2bEligibleVariantIds)
      ? config.b2bEligibleVariantIds.map(Number)
      : []
  );
  const b2bWeightOverrides = new Map(
    Object.entries(config.b2bWeightOverrides || {}).map(([variantId, grams]) => [
      Number(variantId),
      Number(grams),
    ])
  );
  const currentB2BAllowedVariantIds = new Set(
    Array.isArray(config.currentB2BAllowedVariantIds)
      ? config.currentB2BAllowedVariantIds.map(Number)
      : []
  );
  const b2bOptionRestriction = {
    'felting-needle-triangular-gauge-32-36-40': {
      optionName: 'QTY. (required)',
      allowedValues: new Set(['*QTY 50', '*QTY 100']),
      preferredValue: '*QTY 50',
    },
  }[currentProductHandle];

  if (!chargeVariantId) {
    console.error('Trade delivery charge variant is not configured.');
    return;
  }

  let queue = Promise.resolve();
  let scheduledTimer = null;
  let checkoutInProgress = false;

  const cartUpdateUrl = () => window.routes?.cart_update_url || '/cart/update.js';
  const cartAddUrl = () => window.routes?.cart_add_url || '/cart/add.js';

  const requestJson = async (url, body) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    });

    const data = await response.json();
    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'The cart could not be updated.');
    }
    return data;
  };

  const fetchCart = async () => {
    const response = await fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!response.ok) throw new Error('The cart could not be read.');
    return response.json();
  };

  const isChargeItem = (item) =>
    Number(item?.variant_id) === chargeVariantId ||
    String(item?.sku || '').trim().toUpperCase() === chargeSku ||
    String(item?.handle || '') === chargeHandle;

  const isTaggedTradeItem = (item) =>
    typeof item?.sku === 'string' && item.sku.trim().toUpperCase().endsWith('-TRADE');

  const isB2BEligibleItem = (item) => {
    if (!isB2BCustomer || isChargeItem(item)) return false;

    const handle = String(item?.handle || '');
    const productId = Number(item?.product_id || 0);
    const variantId = Number(item?.variant_id || item?.id || 0);
    const sku = String(item?.sku || '').trim().toUpperCase();
    if (
      !eligibleB2BHandles.has(handle) &&
      !eligibleB2BProductIds.has(productId) &&
      !eligibleB2BVariantIds.has(variantId)
    ) {
      return false;
    }

    if (handle === 'maori-master') {
      return /-(20G|100G|5X100G)$/.test(sku);
    }

    if (handle === 'felting-needle-triangular-gauge-32-36-40') {
      return /X(50|100)$/.test(sku);
    }

    return true;
  };

  const isTradeItem = (item) => isTaggedTradeItem(item) || isB2BEligibleItem(item);

  const itemTradeWeight = (item) => {
    const handle = String(item?.handle || '');
    const variantId = Number(item?.variant_id || item?.id || 0);
    const sku = String(item?.sku || '').trim().toUpperCase();

    if (isB2BCustomer && b2bWeightOverrides.has(variantId)) {
      return b2bWeightOverrides.get(variantId);
    }

    // Use the customer-facing pack size for wool so packaging weight never
    // helps a B2B basket reach the 8kg product minimum.
    if (isB2BCustomer && handle === 'maori-master') {
      if (sku.endsWith('-5X100G')) return 500;
      if (sku.endsWith('-20G')) return 20;
      if (sku.endsWith('-100G')) return 100;
    }

    if (isB2BCustomer && handle === 'needle-felting-wool-shade-pack-rolls') {
      return sku.endsWith('-200G') ? 200 : 100;
    }

    if (isB2BCustomer && handle === 'core-wool-needle-felting-baavat-cream') {
      if (sku.endsWith('-500G')) return 500;
      if (sku.endsWith('-100G')) return 100;
    }

    if (
      isB2BCustomer &&
      handle === 'carded-felt-needle-felting-wool-rainbow-colours-multicoloured-batts-7x20g'
    ) {
      return 140;
    }

    return Number(item?.grams || 0);
  };

  const tradeWeight = (cart) =>
    (cart?.items || []).reduce((total, item) => {
      if (!isTradeItem(item)) return total;
      return total + itemTradeWeight(item) * Number(item.quantity || 0);
    }, 0);

  const blockUnavailableB2BVariant = (event) => {
    if (!isB2BCustomer || !restrictCurrentB2BVariants || !currentProductHandle) return;

    const form = event.target.closest?.('form[action*="/cart/add"]');
    if (!form) return;

    const variantId = Number(new FormData(form).get('id') || 0);
    if (currentB2BAllowedVariantIds.has(variantId)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    window.alert('This option is not available in the B2B catalogue. Please choose a trade-priced size.');
  };

  document.addEventListener('submit', blockUnavailableB2BVariant, true);

  const applyB2BOptionRestrictions = () => {
    if (!isB2BCustomer || !b2bOptionRestriction) return;

    const optionInputs = Array.from(document.querySelectorAll('input[type="radio"]')).filter(
      (input) => input.name === b2bOptionRestriction.optionName
    );
    if (!optionInputs.length) return;

    optionInputs.forEach((input) => {
      const isAllowed = b2bOptionRestriction.allowedValues.has(input.value);
      input.disabled = !isAllowed;
      input.hidden = !isAllowed;
      Array.from(input.labels || []).forEach((label) => {
        label.hidden = !isAllowed;
      });
    });

    const selectedInput = optionInputs.find((input) => input.checked);
    const preferredInput = optionInputs.find(
      (input) => input.value === b2bOptionRestriction.preferredValue
    );
    const targetInput =
      selectedInput && b2bOptionRestriction.allowedValues.has(selectedInput.value)
        ? selectedInput
        : preferredInput;
    if (!targetInput) return;

    if (!targetInput.checked) targetInput.labels?.[0]?.click();

    const selectedVariantId = Number(
      document.querySelector('form[action*="/cart/add"] [name="id"]')?.value || 0
    );
    if (!currentB2BAllowedVariantIds.has(selectedVariantId)) {
      targetInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  const updateCart = async (updates, attributes) => {
    const payload = { attributes };
    if (Object.keys(updates).length) payload.updates = updates;
    return requestJson(cartUpdateUrl(), payload);
  };

  const updateWarning = (weight) => {
    const warning = document.querySelector('[data-trade-warning]');
    if (!warning) return;

    const hasTrade = weight > 0;
    const minimumMet = !hasTrade || weight >= minimumTradeWeight;
    warning.style.display = minimumMet ? 'none' : 'block';

    if (!minimumMet) {
      const formatKilograms = (grams) => {
        const decimals = grams % 1000 === 0 ? 0 : grams % 100 === 0 ? 1 : 2;
        return (grams / 1000).toFixed(decimals);
      };
      const kilograms = formatKilograms(weight);
      const remaining = formatKilograms(minimumTradeWeight - weight);
      warning.innerHTML =
        `Trade orders require a minimum of <strong>8kg</strong>. ` +
        `You currently have <strong>${kilograms}kg</strong> of eligible trade products ` +
        `and need another <strong>${remaining}kg</strong>.`;
    }
  };

  const updateCheckoutControls = (cart) => {
    const weight = tradeWeight(cart);
    const hasTrade = weight > 0;
    const minimumMet = !hasTrade || weight >= minimumTradeWeight;

    document.body.classList.toggle('tfb-trade-cart', hasTrade);
    document.body.classList.toggle('tfb-b2b-trade-cart', hasTrade && isB2BCustomer);
    document.body.classList.toggle('tfb-trade-charge-present', cart.items.some(isChargeItem));

    document.querySelectorAll('[name="checkout"], a[href="/checkout"], a[href^="/checkout?"]').forEach((control) => {
      if (minimumMet) {
        control.removeAttribute('disabled');
        control.removeAttribute('aria-disabled');
      } else {
        control.setAttribute('disabled', 'disabled');
        control.setAttribute('aria-disabled', 'true');
      }
    });

    document.querySelectorAll('.additional-checkout-buttons').forEach((element) => {
      element.hidden = hasTrade;
      element.style.display = hasTrade ? 'none' : '';
    });

    updateWarning(weight);
  };

  const decorateChargeRows = () => {
    document.querySelectorAll('.cart-item').forEach((row) => {
      const link = row.querySelector(`a[href*="/products/${chargeHandle}"]`);
      if (!link) return;

      row.dataset.tfbTradeDeliveryCharge = 'true';
      link.removeAttribute('href');
      link.setAttribute('aria-disabled', 'true');
      link.setAttribute('tabindex', '-1');

      const quantityWrapper = row.querySelector('.cart-item__quantity-wrapper');
      if (quantityWrapper) {
        quantityWrapper.innerHTML =
          '<span class="tfb-trade-delivery-lock">Applied once per trade order</span>';
      }
    });
  };

  const applyProtectedButtons = () => {
    if (config.currentProductIsTrade) {
      document.querySelectorAll('.shopify-payment-button').forEach((element) => {
        element.hidden = true;
      });
    }

    if (config.currentProductIsCharge) {
      document.querySelectorAll('product-form, .shopify-payment-button').forEach((element) => {
        element.hidden = true;
      });
    }
  };

  const applyCartState = (cart) => {
    decorateChargeRows();
    applyProtectedButtons();
    updateCheckoutControls(cart);
  };

  const syncOnce = async ({ reloadCartPage = false } = {}) => {
    let cart = await fetchCart();
    const updates = {};
    const taggedTradeItems = cart.items.filter(isTaggedTradeItem);
    const tradeItems = cart.items.filter(isTradeItem);
    const chargeItems = cart.items.filter(isChargeItem);
    const hasTrade = tradeItems.length > 0;
    let changed = false;
    let restrictedItemsRemoved = false;

    if (taggedTradeItems.length && !isTradeCustomer) {
      taggedTradeItems.forEach((item) => {
        updates[item.key] = 0;
      });
      chargeItems.forEach((item) => {
        updates[item.key] = 0;
      });
      await updateCart(updates, {
        tfb_trade_order: '',
        tfb_b2b_order: '',
        tfb_b2b_pilot: '',
      });
      changed = true;
      restrictedItemsRemoved = true;
      cart = await fetchCart();
    } else if (hasTrade && isTradeCustomer) {
      if (!chargeItems.length) {
        await requestJson(cartAddUrl(), {
          items: [
            {
              id: chargeVariantId,
              quantity: 1,
              properties: { _tfb_trade_delivery_charge: 'true' },
            },
          ],
        });
        changed = true;
        cart = await fetchCart();
      }

      const refreshedCharges = cart.items.filter(isChargeItem);
      refreshedCharges.forEach((item, index) => {
        const targetQuantity = index === 0 ? 1 : 0;
        if (Number(item.quantity) !== targetQuantity) {
          updates[item.key] = targetQuantity;
          changed = true;
        }
      });

      const expectedB2B = isB2BCustomer ? 'true' : '';
      if (
        Object.keys(updates).length ||
        cart.attributes?.tfb_trade_order !== 'true' ||
        String(cart.attributes?.tfb_b2b_order || '') !== expectedB2B ||
        cart.attributes?.tfb_b2b_pilot
      ) {
        await updateCart(updates, {
          tfb_trade_order: 'true',
          tfb_b2b_order: expectedB2B,
          tfb_b2b_pilot: '',
        });
        changed = true;
        cart = await fetchCart();
      }
    } else {
      chargeItems.forEach((item) => {
        updates[item.key] = 0;
        changed = true;
      });

      if (
        Object.keys(updates).length ||
        cart.attributes?.tfb_trade_order ||
        cart.attributes?.tfb_b2b_order ||
        cart.attributes?.tfb_b2b_pilot
      ) {
        await updateCart(updates, {
          tfb_trade_order: '',
          tfb_b2b_order: '',
          tfb_b2b_pilot: '',
        });
        changed = true;
        cart = await fetchCart();
      }
    }

    applyCartState(cart);

    if (changed && reloadCartPage && window.location.pathname.replace(/\/$/, '') === '/cart') {
      window.location.reload();
    }

    return { cart, changed, restrictedItemsRemoved };
  };

  const sync = (options = {}) => {
    queue = queue.catch(() => undefined).then(() => syncOnce(options));
    return queue;
  };

  const scheduleSync = (options = {}, delay = 250) => {
    if (scheduledTimer) window.clearTimeout(scheduledTimer);
    scheduledTimer = window.setTimeout(() => {
      sync(options).catch((error) => console.error(error));
    }, delay);
  };

  const prepareCheckout = async () => {
    if (checkoutInProgress) return;
    checkoutInProgress = true;

    try {
      const result = await sync({ reloadCartPage: false });
      const weight = tradeWeight(result.cart);

      if (result.restrictedItemsRemoved) {
        window.location.assign('/cart');
        return;
      }

      if (weight > 0 && weight < minimumTradeWeight) {
        checkoutInProgress = false;
        window.location.assign('/cart#trade-minimum');
        return;
      }

      window.location.assign('/checkout');
    } catch (error) {
      console.error(error);
      checkoutInProgress = false;
      window.location.assign('/cart');
    }
  };

  const isCheckoutControl = (element) => {
    if (!(element instanceof Element)) return false;
    return Boolean(
      element.closest('[name="checkout"], a[href="/checkout"], a[href^="/checkout?"]')
    );
  };

  document.addEventListener(
    'click',
    (event) => {
      if (!isCheckoutControl(event.target)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      prepareCheckout();
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.querySelector('[name="checkout"]')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      prepareCheckout();
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.matches('form[action*="/cart/add"]')) return;

      scheduleSync({ reloadCartPage: false }, 700);
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('button[name="add"], .wool-shade-card__alternative-add');
      if (!button) return;
      scheduleSync({ reloadCartPage: false }, 700);
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      if (!isB2BCustomer) return;

      const removeControl = event.target.closest(
        'cart-remove-button, .cart-remove-button, a[href*="/cart/change"]'
      );
      if (!removeControl) return;

      const link = removeControl.matches?.('a[href*="/cart/change"]')
        ? removeControl
        : removeControl.querySelector?.('a[href*="/cart/change"]');
      if (!link?.href) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      window.location.assign(link.href);
    },
    true
  );

  const initialise = () => {
    if (config.currentProductIsTrade) document.body.classList.add('tfb-trade-product');
    if (config.currentProductIsCharge) document.body.classList.add('tfb-trade-charge-product');
    if (isB2BCustomer) document.body.classList.add('tfb-b2b-customer');

    applyB2BOptionRestrictions();
    window.setTimeout(applyB2BOptionRestrictions, 250);
    decorateChargeRows();
    applyProtectedButtons();
    sync({ reloadCartPage: true }).catch((error) => console.error(error));

    const observer = new MutationObserver((mutations) => {
      const cartChanged = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some(
          (node) =>
            node instanceof Element &&
            (node.matches?.('#main-cart-items, #main-cart-footer, cart-notification, .cart-item') ||
              node.querySelector?.('#main-cart-items, #main-cart-footer, cart-notification, .cart-item'))
        )
      );

      applyB2BOptionRestrictions();
      decorateChargeRows();
      applyProtectedButtons();
      if (cartChanged) scheduleSync({ reloadCartPage: true }, 180);
    });

    observer.observe(document.body, { childList: true, subtree: true });
  };

  window.TFBTradeDelivery = {
    sync,
    fetchCart,
    isTradeItem,
    isChargeItem,
    tradeWeight,
    prepareCheckout,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, { once: true });
  } else {
    initialise();
  }

  window.addEventListener('pageshow', () => scheduleSync({ reloadCartPage: true }, 100));
})();
